import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Injectable } from '@angular/core';
import { Subject, lastValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EndPointApi } from '@helpers/endpointapi';
import { Shipyard } from 'icreate/common/models/shipyard';

@Injectable({
    providedIn: 'root',
})
export class ShipyardService {
    private domain: string;
    private headers: any = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    private shipyardList: Shipyard[];

    private shipyardListUpdated$: Subject<void> = new Subject<void>();
    private selectedShipyardSubject = new BehaviorSubject<string>(null);
    readonly selectedShipyard$ = this.selectedShipyardSubject.asObservable();

    constructor(private http: HttpClient) {
        this.domain = EndPointApi.iApiDomain();
    }

    async getList() {
        if (this.shipyardList) {
            return this.shipyardList;
        }
        const list = await lastValueFrom<Shipyard[]>(this.http.get<Shipyard[]>(`${this.domain}/api/shipyard`));
        this.shipyardList = list.sort((a, b) =>
            a.name.localeCompare(b.name, ['en', 'jp'], {
                numeric: true,
                sensitivity: 'variant',
            })
        );
        return this.shipyardList;
    }

    async create(shipyardName: string, iApiId: string) {
        const shipyardData = {
            name: shipyardName,
            iApiId: iApiId,
        };
        return lastValueFrom(
            this.http.post(`${this.domain}/api/shipyard`, shipyardData, {
                headers: this.headers,
            })
        );
    }

    async duplicate(shipyard: Shipyard) {
        return lastValueFrom(
            this.http.post(`${this.domain}/api/shipyard/duplicate`, shipyard, {
                headers: this.headers,
            })
        );
    }

    async update(shipyardName: string, shipyard: Shipyard) {
        const shipyardData = {
            name: shipyardName,
            iApiCompanyId: shipyard.iApiCompanyId,
            skipGuard: true,
        };
        return lastValueFrom(
            this.http.put(`${this.domain}/api/shipyard/${shipyard.id}`, shipyardData, {
                headers: this.headers,
            })
        );
    }

    async delete(shipyardId: string) {
        return lastValueFrom(
            this.http.delete(`${this.domain}/api/shipyard/${shipyardId}`, {
                body: { skipGuard: true },
            })
        );
    }

    getUpdatedList() {
        return this.shipyardList;
    }

    updateShipyardList(updatedShipyardList: Shipyard[]) {
        this.shipyardList = updatedShipyardList.sort((a, b) =>
            a.name.localeCompare(b.name, ['en', 'jp'], {
                numeric: true,
                sensitivity: 'variant',
            })
        );
        this.shipyardListUpdated$.next();
    }

    listUpdatedSubject(): Subject<void> {
        return this.shipyardListUpdated$;
    }

    async getiAPIShipyards(): Promise<{ id: string; name: string }[]> {
        return await firstValueFrom(this.http.get<{ id: string; name: string }[]>('/api/shipyard/iAPI'));
    }

    setSelectedShipyard(shipyard: string) {
        this.selectedShipyardSubject.next(shipyard);
    }
}
